/**
 * Shared Error Handling Utilities for Ticket Frontend
 *
 * Handles parsing of Frappe API error responses, including the
 * triple-nested _server_messages format used by frappe.throw().
 */

/**
 * Parse Frappe _server_messages format (triple-nested JSON)
 *
 * Frappe returns error messages in this format:
 * "[\"{ \\\"message\\\": \\\"actual error message\\\" }\"]"
 *
 * @param {string} serverMessages - The _server_messages field from API response
 * @returns {string|null} Extracted message or null if parsing fails
 */
export function parseFrappeServerMessages(serverMessages) {
  if (!serverMessages) return null;

  const parseMaybeJson = (value) => {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const extractMessage = (value) => {
    const parsed = parseMaybeJson(value);
    if (!parsed) return null;

    if (typeof parsed === 'string') {
      const trimmed = parsed.trim();
      if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('[')) return null;
      return trimmed;
    }

    if (Array.isArray(parsed)) {
      for (const entry of parsed) {
        const nested = extractMessage(entry);
        if (nested) return nested;
      }
      return null;
    }

    if (typeof parsed === 'object') {
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message.trim();
      }
      if (typeof parsed.toast === 'string' && parsed.toast.trim()) {
        return parsed.toast.trim();
      }
    }
    return null;
  };

  try {
    const parsed = extractMessage(serverMessages);
    if (parsed) return parsed;
  } catch {
    if (typeof serverMessages === 'string' && serverMessages.length < 500) {
      return serverMessages.trim() || null;
    }
  }

  return null;
}

/**
 * Extract user-friendly error message from API response
 *
 * Priority order:
 * 1. response.toast (canonical API schema: json_ok/json_err)
 * 2. response._server_messages (Frappe exceptions via frappe.throw())
 * 3. fallback
 *
 * @param {Object} response - API response object
 * @param {string} fallback - Fallback message if no error found
 * @returns {string} User-friendly error message
 */
export function extractErrorMessage(response, fallback) {
  if (fallback === undefined) {
    fallback = (typeof __ === 'function') ? __('Ein Fehler ist aufgetreten.') : 'Ein Fehler ist aufgetreten.';
  }
  if (!response) return fallback;

  // 1. toast field (canonical API schema: json_ok/json_err)
  if (typeof response.toast === 'string' && response.toast.trim()) {
    return response.toast;
  }

  // 2. _server_messages (Frappe exceptions via frappe.throw())
  const candidates = [
    response,
    response.responseJSON,
    response.payload,
    response.data,
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (candidate?._server_messages) {
      const parsed = parseFrappeServerMessages(candidate._server_messages);
      if (parsed) return parsed;
    }
  }

  return fallback;
}

/**
 * Check if API response indicates an error
 *
 * @param {Object} response - API response object
 * @returns {boolean} True if response is an error
 */
export function isErrorResponse(response) {
  if (!response) return true;
  if (response.success === false) return true;
  if (response._server_messages) return true;
  return false;
}

/**
 * Extract error code from API response
 *
 * @param {Object} response - API response object
 * @returns {string|null} Error code or null
 */
export function extractErrorCode(response) {
  if (!response) return null;

  return response.code || null;
}

/**
 * Check if an error is likely a network/transient error that could succeed on retry.
 *
 * @param {Object} response - API response object or error
 * @returns {boolean} True if a retry might help
 */
export function isRetryableError(response) {
  if (!response) return true;
  const RETRYABLE_CODES = [
    'service_unavailable', 'bad_gateway', 'gateway_timeout',
    'fetch_failed', 'network_error', 'timeout',
  ];
  const code = response.code || response.error_code || '';
  if (RETRYABLE_CODES.includes(code)) return true;
  // Detect fetch/network failures
  if (response instanceof TypeError && response.message === 'Failed to fetch') return true;
  return false;
}

export default {
  parseFrappeServerMessages,
  extractErrorMessage,
  isErrorResponse,
  extractErrorCode,
  isRetryableError,
};
